import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Plus, Search, X, Edit3, Trash2, MapPin, Bed, Bath, Maximize } from "lucide-react";

const TYPES = ["apartment", "house", "villa", "studio", "commercial", "land", "other"];

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editProp, setEditProp] = useState(null);

  const fetchProperties = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get("/properties", { params });
      setProperties(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProperties(); }, [search]);

  const handleCreate = async (form) => {
    try {
      await api.post("/properties", form);
      setShowCreate(false);
      fetchProperties();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleUpdate = async (id, form) => {
    try {
      await api.put(`/properties/${id}`, form);
      setEditProp(null);
      fetchProperties();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/properties/${id}`);
      fetchProperties();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="properties-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#868E96]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9 pr-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm w-60"
            placeholder="Search properties..."
            data-testid="properties-search-input"
          />
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] transition-colors flex items-center gap-2 rounded-sm"
          data-testid="create-property-button"
        >
          <Plus size={14} /> New Property
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-sm text-[#868E96] py-8 text-center">Loading...</p>
      ) : properties.length === 0 ? (
        <div className="border border-border bg-white p-12 text-center">
          <p className="text-sm text-[#868E96]">No properties yet. Add your first property.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((prop, i) => (
            <div key={prop.id} className="border border-border bg-white hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`property-card-${prop.id}`}>
              <div className="h-36 bg-[#F1F3F5] flex items-center justify-center overflow-hidden">
                <img src="https://images.unsplash.com/photo-1706808849827-7366c098b317?w=400" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo' }}>{prop.title}</h4>
                    <div className="flex items-center gap-1 text-xs text-[#868E96] mt-0.5">
                      <MapPin size={11} />{prop.location}
                    </div>
                  </div>
                  <span className={`status-badge status-${prop.status}`}>{prop.status}</span>
                </div>
                <p className="text-xl font-black text-[#002FA7] my-2" style={{ fontFamily: 'Chivo' }}>
                  ${prop.price?.toLocaleString()}
                </p>
                <div className="flex items-center gap-4 text-xs text-[#495057]">
                  <span className="flex items-center gap-1"><Bed size={12} />{prop.bedrooms} bed</span>
                  <span className="flex items-center gap-1"><Bath size={12} />{prop.bathrooms} bath</span>
                  <span className="flex items-center gap-1"><Maximize size={12} />{prop.area} m²</span>
                </div>
                {prop.features?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {prop.features.slice(0, 4).map((f, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 bg-[#F1F3F5] text-[#495057] border border-border">{f}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                  <button onClick={() => setEditProp(prop)} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm" data-testid={`edit-property-${prop.id}`}><Edit3 size={14} className="text-[#495057]" /></button>
                  <button onClick={() => handleDelete(prop.id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`delete-property-${prop.id}`}><Trash2 size={14} className="text-[#C92A2A]" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreate || editProp) && (
        <PropertyFormModal
          property={editProp}
          onSave={editProp ? (data) => handleUpdate(editProp.id, data) : handleCreate}
          onClose={() => { setShowCreate(false); setEditProp(null); }}
        />
      )}
    </div>
  );
}

function PropertyFormModal({ property, onSave, onClose }) {
  const [form, setForm] = useState({
    title: property?.title || "",
    description: property?.description || "",
    price: property?.price || 0,
    location: property?.location || "",
    property_type: property?.property_type || "apartment",
    bedrooms: property?.bedrooms || 0,
    bathrooms: property?.bathrooms || 0,
    area: property?.area || 0,
    features: property?.features || [],
    status: property?.status || "available",
  });
  const [featureInput, setFeatureInput] = useState("");

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm({ ...form, features: [...form.features, featureInput.trim()] });
      setFeatureInput("");
    }
  };

  const removeFeature = (idx) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white border border-border p-6 w-[32rem] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="property-form-modal">
        <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>{property ? "Edit Property" : "New Property"}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Title *</label>
            <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Price *</label>
              <input type="number" required value={form.price} onChange={e => setForm({...form, price: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-price-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Location *</label>
              <input type="text" required value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-location-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Type</label>
              <select value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-type-select">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Bedrooms</label>
              <input type="number" value={form.bedrooms} onChange={e => setForm({...form, bedrooms: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-bedrooms-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Bathrooms</label>
              <input type="number" value={form.bathrooms} onChange={e => setForm({...form, bathrooms: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-bathrooms-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Area (m²)</label>
              <input type="number" value={form.area} onChange={e => setForm({...form, area: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-area-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="property-status-select">
                <option value="available">Available</option>
                <option value="under_offer">Under Offer</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Features</label>
            <div className="flex gap-2">
              <input type="text" value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFeature())} className="flex-1 h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" placeholder="e.g. balcony, parking" data-testid="property-feature-input" />
              <button type="button" onClick={addFeature} className="h-9 px-3 border border-border text-sm hover:bg-[#F1F3F5] rounded-sm" data-testid="add-feature-button">Add</button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#F1F3F5] border border-border">
                  {f}
                  <button type="button" onClick={() => removeFeature(i)}><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full h-20 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none" data-testid="property-description-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-9 px-4 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]" data-testid="property-form-cancel">Cancel</button>
            <button type="submit" className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="property-form-submit">{property ? "Update" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
